import React, { useState, useEffect, useRef } from "react";
import moment from "moment-timezone";
import { Constants, getJudgeEntryLimitKey, getShowJudgesForEntryLimits, showOffersDetective } from "@eagerdog/constants";
import { IEvent, IShow, IShowElement } from "@eagerdog/interfaces";
import { apiService } from "src/services";
import { helperService } from "src/services/helper.service";

import Input from "src/components/Input/Input";
import Checkbox, { ICheck } from "src/components/Checkbox/Checkbox";
import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import Button from "src/components/Button/Button";
import DatePick from "src/components/DatePick/DatePick";
import { toast } from "src/components/Toast/ToastManager";

import styles from "./WaitlistTab.module.scss";

interface IProps {
  event: IEvent,
  show: IShow,
  showType: string,
  showElements: ICheck[],
  onUpdate?: () => void,
}

interface IPropsWaitlistElement {
  waitlist: IWaitlistItem,
  index: number,
  isHerding?: boolean,
  onChange(wl: IWaitlistItem, index: number): void,
  onDelete?(index: number): void,
}

export interface IWaitlistItem {
  waitlist_option: string,
  show_element: string,
  level?: string,
  section?: string,
  max_entries: string,
  waitlist_size: string,
  waitlist_enabled: boolean,
}

const HERDING_SHOW_TYPES = [
  Constants.show_type.herding,
  Constants.show_type.pre_trial_herding,
  Constants.show_type.instinct_test_herding,
];

interface IJudgeLimitRow {
  judge_name: string,
  judge_number?: string,
  total_entries: string,
  manual_entries: string,
}

const STOCK_OPTIONS: IOption[] = [
  { value: "Sheep", id: Constants.dog_class_section.sheep },
  { value: "Cattle", id: Constants.dog_class_section.cattle },
  { value: "Ducks", id: Constants.dog_class_section.ducks },
];

const WaitlistElement: React.FC<IPropsWaitlistElement> = (props) => {
  const [waitlist, setWaitlist] = useState<IWaitlistItem>(props.waitlist);

  const handleWaitlistToggle = () => {
    let _waitlist:IWaitlistItem = { ...waitlist, waitlist_enabled: !waitlist.waitlist_enabled };
    setWaitlist(_waitlist);
    props.onChange(_waitlist, props.index);
  }

  const handleMaxEntriesChange = (e: any) => {
    let _waitlist:IWaitlistItem = { ...waitlist, max_entries: e.target.value };
    setWaitlist(_waitlist);
    props.onChange(_waitlist, props.index);
  }

  const handleWaitlistSizeChange = (e: any) => {
    let _waitlist:IWaitlistItem = { ...waitlist, waitlist_size: e.target.value };
    setWaitlist(_waitlist);
    props.onChange(_waitlist, props.index);
  }

  // For herding section-only rows, show just the stock name; otherwise full Course / Level / Stock path
  const displayLabel = waitlist.section && !waitlist.show_element
    ? waitlist.section
    : waitlist.section
      ? `${waitlist.show_element}${waitlist.level ? " / " + waitlist.level : ""} / ${waitlist.section}`
      : waitlist.show_element;

  return(
    <div className={waitlist.waitlist_enabled ? styles.WaitlistElement : `${styles.WaitlistElement} ${styles.disabled}`}>
      <div className={styles.title}>
        <div className={styles.titleInner}>{displayLabel}</div>
        <div className={styles.toggle}>
          <Checkbox id={displayLabel + "check"} onChange={handleWaitlistToggle} value={waitlist.waitlist_enabled} label={"Enable Waitlist"} />
          {props.isHerding && props.onDelete && (
            <button
              type="button"
              className={styles.deleteRow}
              onClick={() => props.onDelete!(props.index)}
              title="Remove limit row"
            >✕</button>
          )}
        </div>
      </div>
      <div className={styles.data}>
        <div className={styles.inputWrap}>
          <Input
            className={styles.input}
            onChange={handleMaxEntriesChange}
            type="text"
            label="Entry Limit"
            placeholder="What's the maximum entries?"
            defaultValue={waitlist.max_entries}
          />
        </div>
        <div className={styles.inputWrap}>
          <Input
            className={styles.input}
            required={waitlist.waitlist_enabled ? true : false}
            disabled={waitlist.waitlist_enabled ? false : true}
            onChange={handleWaitlistSizeChange}
            type="text"
            label="Waitlist Size"
            placeholder="What's the waitlist size?"
            defaultValue={waitlist.waitlist_size}
          />
        </div>
      </div>
    </div>
  );
}

const WaitlistTab: React.FC<IProps> = (props) => {
  // Regular Herding: stock is in section, show_elements have section set
  const isHerdingShow = props.show.show_type === Constants.show_type.herding;

  // AKC Scentwork: Detective Run Limit is a secondary cap shown regardless of Limit Type,
  // only when Detective is offered in the show setup
  const offersDetectiveLimit = props.event.sanctioning_club === Constants.sanctioning_club.AKC &&
    props.showType === Constants.show_type.scentwork &&
    showOffersDetective(props.show.show_elements);

  const getWaitListSizingLabel = (sizing: string) => {
    let label:string = "Show Entry Limit";

    switch (sizing) {
      case Constants.waitlist_options.per_level_limit:
        label = isHerdingShow ? "Per Stock Limit" : "Per Level Limit";
      break;
      case Constants.waitlist_options.daily_judge_time_limit:
        label = "Daily Judge Limit";
      break;
      case Constants.waitlist_options.daily_trial_time_limit:
        label = "Judge Hours Per Trial";
      break;
      case Constants.waitlist_options.daily_judge_mrv_limit:
        label = "BHA Daily Judge MRV";
      break;
      case Constants.waitlist_options.show_dog_limit:
        label = "Show Dog Limit";
        break;
      case Constants.waitlist_options.entries_per_judge:
        label = "Entries per Judge";
        break;
    }

    return label;
  }

  const getWaitlistSizingValue = () => {
    if (props.show.waitlist_option && props.show.waitlist_option !== Constants.waitlist_options.none) {
      return { id: props.show.waitlist_option, value: getWaitListSizingLabel(props.show.waitlist_option) };
    }

    return { id: Constants.waitlist_options.show_entry_limit, value: getWaitListSizingLabel(Constants.waitlist_options.show_entry_limit) };
  }

  const [waitlists, setWaitlists] = useState<IWaitlistItem[]>([]);
  const [updatingShow, setUpdatingShow] = useState<boolean>(false);
  const [show, setShow] = useState<IShow>(props.show);
  const [waitlistSizing, setWaitlistSizing] = useState<IOption>(getWaitlistSizingValue());
  const [waitlistLimit, setWaitlistLimit] = useState<string>();
  const [showWaitlistEnabled, setShowWaitlistEnabled] = useState<boolean>(false);
  const [showWaitlistSize, setShowWaitlistSize] = useState<string>("25");
  const [detectiveRunLimit, setDetectiveRunLimit] = useState<string>(props.show.detective_run_limit?.toString() || "");
  const [detectiveManualEntries, setDetectiveManualEntries] = useState<string>(props.show.detective_manual_entries?.toString() || "");

  // Add-row state for herding per-stock limits
  const [newRowSection, setNewRowSection] = useState<IOption>({ value: "", id: "" });

  // Track which show._id has been initialized for herding so re-renders don't reset unsaved rows
  const herdingInitializedRef = useRef<string | null>(null);

  const waitlistSizingOptions:IOption[] = [
    { id: Constants.waitlist_options.daily_judge_time_limit, value: "Daily Judge Limit" },
    { id: Constants.waitlist_options.show_entry_limit, value: "Show Entry Limit" },
    {
      id: Constants.waitlist_options.per_level_limit,
      value: isHerdingShow ? "Per Stock Limit" : "Per Level Limit",
    },
    { id: Constants.waitlist_options.daily_trial_time_limit, value: "Judge Hours Per Trial" },
    { id: Constants.waitlist_options.show_dog_limit, value: "Show Dog Limit" },
    { id: Constants.waitlist_options.daily_judge_mrv_limit, value: "BHA Daily Judge MRV" },
    { id: Constants.waitlist_options.entries_per_judge, value: "Entries per Judge" },
  ];

  const timezone = props.event.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const isEntriesPerJudge = waitlistSizing.id === Constants.waitlist_options.entries_per_judge;

  const buildJudgeLimitRows = (_show: IShow): IJudgeLimitRow[] => {
    const saved = new Map(
      (_show.judge_entry_limits || []).map((l) => [getJudgeEntryLimitKey(l.judge_name, l.judge_number), l])
    );
    return getShowJudgesForEntryLimits(_show).map((judge) => {
      const limit = saved.get(getJudgeEntryLimitKey(judge.judge_name, judge.judge_number));
      return {
        judge_name: judge.judge_name,
        judge_number: judge.judge_number,
        total_entries: limit?.total_entries?.toString() || "",
        manual_entries: limit?.manual_entries ? limit.manual_entries.toString() : "",
      };
    });
  };

  const [judgeLimitRows, setJudgeLimitRows] = useState<IJudgeLimitRow[]>(buildJudgeLimitRows(props.show));
  const [manualEntryLimitDate, setManualEntryLimitDate] = useState<Date | undefined>(
    props.show.manual_entry_limit_date
      ? helperService.getDateTimeWithTimezone(timezone, props.show.manual_entry_limit_date)
      : undefined
  );

  useEffect(() => {
    setShow(props.show);
    setJudgeLimitRows(buildJudgeLimitRows(props.show));
    setManualEntryLimitDate(
      props.show.manual_entry_limit_date
        ? helperService.getDateTimeWithTimezone(timezone, props.show.manual_entry_limit_date)
        : undefined
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.show, timezone]);

  const showDetectiveManualEntries = offersDetectiveLimit && isEntriesPerJudge;
  const hasDetectiveManualEntries = showDetectiveManualEntries && Number(detectiveManualEntries) > 0;
  const hasManualEntries = judgeLimitRows.some((r) => Number(r.manual_entries) > 0) || hasDetectiveManualEntries;

  const classesWithoutJudge: string[] = [...new Set(
    (props.show.show_elements || [])
      .filter((se: IShowElement) => !se.judge_name?.trim())
      .map((se: IShowElement) => [se.show_element, se.level].filter(Boolean).join(" "))
  )];

  const updateJudgeLimitRow = (index: number, field: "total_entries" | "manual_entries", value: string) => {
    setJudgeLimitRows((rows) => rows.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  const getJudgeLimitError = (): string | undefined => {
    if (judgeLimitRows.length === 0) {
      return "Assign judges in the Judges or Judge Schedules tab before setting Entries per Judge.";
    }
    for (const row of judgeLimitRows) {
      const total = Number(row.total_entries);
      const manual = row.manual_entries ? Number(row.manual_entries) : 0;
      if (!row.total_entries || !Number.isInteger(total) || total < 1) {
        return `Enter a Total Entries value of at least 1 for Judge ${row.judge_name}.`;
      }
      if (!Number.isInteger(manual) || manual < 0) {
        return `Manual Entries for Judge ${row.judge_name} must be 0 or more.`;
      }
      if (manual > total) {
        return `Manual Entries for Judge ${row.judge_name} can't be more than Total Entries.`;
      }
    }
    if (showDetectiveManualEntries && detectiveManualEntries) {
      const detectiveManual = Number(detectiveManualEntries);
      if (!Number.isInteger(detectiveManual) || detectiveManual < 0) {
        return "Detective Manual Entries must be 0 or more.";
      }
      if (detectiveManual > 0 && !(Number(detectiveRunLimit) > 0)) {
        return "Set a Detective Run Limit before setting Detective Manual Entries.";
      }
      if (detectiveManual > Number(detectiveRunLimit)) {
        return "Detective Manual Entries can't be more than the Detective Run Limit.";
      }
    }
    if (hasManualEntries && !manualEntryLimitDate) {
      return "Set a Manual Entry Limit Date/Time when any Manual Entries are set.";
    }
    return undefined;
  };

  const lotteryWaitlistSizingOptions:IOption[] = [
    { id: Constants.waitlist_options.daily_judge_time_limit, value: "Daily Judge Limit" },
    { id: Constants.waitlist_options.show_entry_limit, value: "Show Entry Limit" },
  ];

  // All unique stock types offered in this show (for the Add Row dropdown)
  const herdingStockOptions: IOption[] = isHerdingShow
    ? [...new Set(
        (props.show.show_elements || [])
          .map((se: any) => se.section as string)
          .filter(Boolean)
      )].map(section => ({ value: section, id: section }))
    : [];

  useEffect(() => {
    let _wls:IWaitlistItem[] = [];
    const showId = (props.show as any)?._id?.toString() ?? null;

    if (props.show) {
      if (isHerdingShow) {
        // Only reload from DB when we haven't initialized for this show yet.
        // Using a ref so adding/removing rows (which change waitlists.length)
        // doesn't trigger an overwrite of unsaved changes.
        if (herdingInitializedRef.current !== showId) {
          // Collect one row per unique section (stock type); deduplicate across courses/levels
          const seenSections = new Set<string>();
          _wls = [];
          for (const _se of (props.show.show_elements || []) as any[]) {
            if (_se.section && _se.max_entries != null && !seenSections.has(_se.section)) {
              seenSections.add(_se.section);
              _wls.push({
                waitlist_option: "None",
                show_element: "",
                section: _se.section,
                max_entries: _se.max_entries?.toString() || "",
                waitlist_size: _se.waitlist_size?.toString() || "25",
                waitlist_enabled: _se.waitlist_enabled || false,
              });
            }
          }
          setWaitlists(_wls);
          herdingInitializedRef.current = showId;
        }
        return;
      }

      // Non-herding: existing logic
      let _showElements:ICheck[] = props.showElements.filter((_se: any) => _se.checked);

      if (waitlists.length === 0) {
        for (let s in props.show.show_elements) {
          let _se:any = props.show.show_elements[s];

          _wls.push({
            waitlist_option: _se.waitlist_option || "None",
            show_element: _se.level ? _se.level : _se.show_element,
            max_entries: _se.max_entries?.toString() || "500",
            waitlist_size: _se.waitlist_size || "25",
            waitlist_enabled: _se.waitlist_enabled ? _se.waitlist_enabled : false
          });
        }
      } else {
        if (props.showType === props.show.show_type) {
          for (let s in _showElements) {
            let _se:any = _showElements[s];
            let hasValue = props.show.show_elements.filter((se: any) => { return se.show_element === _se.label || se.level === _se.label; });

            _wls.push({
              waitlist_option: _se.waitlist_option || "None",
              show_element: hasValue.length > 0 ? hasValue[0].level ? hasValue[0].level : hasValue[0].show_element : _se.label,
              max_entries: hasValue.length > 0 ? hasValue[0].max_entries?.toString() || "500" : "500",
              waitlist_size: hasValue.length > 0 ? hasValue[0].waitlist_size?.toString() || "25" : "25",
              waitlist_enabled: hasValue.length > 0 ? hasValue[0].waitlist_enabled || false : false
            });
          }
        } else {
          for (let s in _showElements) {
            let _se:any = _showElements[s];
            _wls.push({
              waitlist_option: _se.waitlist_option || "None",
              show_element:  _se.label,
              max_entries: "500",
              waitlist_size: "25",
              waitlist_enabled: false
            });
          }
        }
      }
    } else {
      let _showElements:ICheck[] = props.showElements.filter((_se: any) => _se.checked);
      for (let s in _showElements) {
        let _se:any = _showElements[s];

        _wls.push({
          waitlist_option: _se.waitlist_option || "None",
          show_element: _se.label,
          max_entries: "500",
          waitlist_size: "25",
          waitlist_enabled: false
        });
      }
    }

    if (_wls.length > 0) {
      setWaitlists(_wls);
    }
  }, [props.showElements, props.show, waitlists.length, props.showType, isHerdingShow]);

  const updateWaitlist = (_wl: IWaitlistItem, _index: number) => {
    let _waitlists:IWaitlistItem[] = [...waitlists];
    _waitlists[_index] = _wl;
    setWaitlists(_waitlists);
  }

  const deleteHerdingRow = (index: number) => {
    setWaitlists(waitlists.filter((_, i) => i !== index));
  }

  const addHerdingRow = () => {
    if (!newRowSection.id) {
      toast.show({
        title: "Missing Stock Type",
        content: "Please select a stock type before adding a limit row.",
        duration: 8000,
        type: "fail"
      });
      return;
    }

    // Validate the stock type is used in this show
    const exists = (props.show.show_elements || []).some((se: any) => se.section === newRowSection.id);
    if (!exists) {
      toast.show({
        title: "Invalid Selection",
        content: `${newRowSection.value} is not offered in this show.`,
        duration: 8000,
        type: "fail"
      });
      return;
    }

    // Prevent duplicates
    if (waitlists.find(w => w.section === newRowSection.id)) {
      toast.show({
        title: "Duplicate Row",
        content: `A limit row for ${newRowSection.value} already exists.`,
        duration: 8000,
        type: "fail"
      });
      return;
    }

    setWaitlists([...waitlists, {
      waitlist_option: "None",
      show_element: "",
      section: newRowSection.id,
      max_entries: "",
      waitlist_size: "25",
      waitlist_enabled: false,
    }]);
    setNewRowSection({ value: "", id: "" });
  }

  const updateShowWaitlist = (_waitlist: IWaitlistItem[]) => {
    if (show && !updatingShow) {
      if (showWaitlistEnabled && (isNaN(Number(showWaitlistSize)) || Number(showWaitlistSize) <= 0)) {
        toast.show({
          title: "Invalid Waitlist Size",
          content: "Waitlist size must be greater than 0 when the waitlist is enabled.",
          duration: 8000,
          type: "error"
        });
        return;
      }

      if (offersDetectiveLimit && detectiveRunLimit && (isNaN(Number(detectiveRunLimit)) || Number(detectiveRunLimit) <= 0)) {
        toast.show({
          title: "Invalid Detective Run Limit",
          content: "Detective Run Limit must be greater than 0, or left empty for no restriction.",
          duration: 8000,
          type: "error"
        });
        return;
      }

      const judgeLimitError = isEntriesPerJudge ? getJudgeLimitError() : undefined;
      if (judgeLimitError) {
        toast.show({
          title: "Entries per Judge",
          content: judgeLimitError,
          duration: 8000,
          type: "error"
        });
        return;
      }

      const invalidPerLevel = _waitlist.find(w => w.waitlist_enabled && (isNaN(Number(w.waitlist_size)) || Number(w.waitlist_size) <= 0));
      if (invalidPerLevel) {
        const label = invalidPerLevel.section && !invalidPerLevel.show_element
          ? invalidPerLevel.section
          : invalidPerLevel.section
            ? `${invalidPerLevel.show_element} / ${invalidPerLevel.level} / ${invalidPerLevel.section}`
            : invalidPerLevel.show_element;
        toast.show({
          title: "Invalid Waitlist Size",
          content: `Waitlist size for "${label}" must be greater than 0 when the waitlist is enabled.`,
          duration: 8000,
          type: "error"
        });
        return;
      }

      setUpdatingShow(true);

      let showElementsPayload: any[];

      if (isHerdingShow && waitlistSizing.id === Constants.waitlist_options.per_level_limit) {
        // Herding per-stock: apply the stock-wide limit to ALL show_elements that share
        // that section (stock type), regardless of course or level.
        showElementsPayload = show.show_elements.map((s: IShowElement) => {
          const sAny = s as any;
          const limitRow = _waitlist.find(w => w.section === sAny.section);

          if (limitRow) {
            const updated: any = {
              ...s,
              max_entries: Number(limitRow.max_entries) || 0,
              waitlist_enabled: limitRow.waitlist_enabled,
            };
            if (limitRow.waitlist_enabled) {
              updated.waitlist_size = Number(limitRow.waitlist_size) || 0;
            } else {
              delete updated.waitlist_size;
            }
            return updated;
          } else {
            // No limit for this stock — clear any previously-set limit data
            const cleared: any = { ...s };
            delete cleared.max_entries;
            delete cleared.waitlist_enabled;
            delete cleared.waitlist_size;
            return cleared;
          }
        });
      } else {
        // Standard per-level or clearing limits
        showElementsPayload = show.show_elements.map((s: IShowElement) => {
          if (waitlistSizing.id === Constants.waitlist_options.per_level_limit) {
            let hasWaitlist = _waitlist.filter((w: IWaitlistItem) => { return (s as any).level ? w.show_element === (s as any).level : w.show_element === s.show_element; });

            if (hasWaitlist.length > 0) {
              let se: any = { ...s, max_entries: Number(hasWaitlist[0].max_entries), waitlist_option: hasWaitlist[0].waitlist_option };

              if (hasWaitlist[0].waitlist_enabled) {
                se.waitlist_enabled = true;
                se.waitlist_size = Number(hasWaitlist[0].waitlist_size);
              }

              return se;
            } else {
              return s;
            }
          } else {
            let se: any = { ...s };

            delete se.waitlist_enabled;
            delete se.waitlist_size;
            delete se.max_entries;

            return se;
          }
        });
      }

      let payload:any = {
        ...show,
        waitlist_option: waitlistSizing.id,
        show_elements: showElementsPayload,
      };

      if (waitlistSizing.id === Constants.waitlist_options.daily_judge_time_limit ||
        waitlistSizing.id === Constants.waitlist_options.daily_trial_time_limit ||
        waitlistSizing.id === Constants.waitlist_options.daily_judge_mrv_limit ||
        waitlistSizing.id === Constants.waitlist_options.show_entry_limit  ||
        waitlistSizing.id === Constants.waitlist_options.show_dog_limit ||
        isEntriesPerJudge) {
        payload.waitlist_limit = Number(waitlistLimit);
        payload.waitlist_enabled = showWaitlistEnabled;
        if (showWaitlistEnabled) {
          payload.waitlist_size = Number(showWaitlistSize);
        } else {
          payload.waitlist_size = undefined;
        }
      } else {
        payload.waitlist_limit = undefined;
        payload.waitlist_enabled = undefined;
        payload.waitlist_size = undefined;
      }

      if (isEntriesPerJudge) {
        payload.judge_entry_limits = judgeLimitRows.map((r) => ({
          judge_name: r.judge_name,
          ...(r.judge_number ? { judge_number: r.judge_number } : {}),
          total_entries: Number(r.total_entries),
          ...(Number(r.manual_entries) > 0 ? { manual_entries: Number(r.manual_entries) } : {}),
        }));
        payload.waitlist_limit = payload.judge_entry_limits.reduce((sum: number, l: any) => sum + l.total_entries, 0);
        payload.manual_entry_limit_date = hasManualEntries && manualEntryLimitDate
          ? moment.tz(moment(manualEntryLimitDate).format("YYYY-MM-DD HH:mm"), timezone).toISOString()
          : undefined;
      } else {
        payload.judge_entry_limits = undefined;
        payload.manual_entry_limit_date = undefined;
      }

      // Detective Run Limit is independent of waitlistSizing - always included when AKC Scentwork
      payload.detective_run_limit = offersDetectiveLimit && detectiveRunLimit && Number(detectiveRunLimit) > 0
        ? Number(detectiveRunLimit)
        : undefined;
      payload.detective_manual_entries = hasDetectiveManualEntries && payload.detective_run_limit
        ? Number(detectiveManualEntries)
        : undefined;

      delete payload.__v;
      
      apiService.updateShow(props.event._id, show._id, payload).then((response) => {
        setShow(response);
        setUpdatingShow(false);
        
        if (props.onUpdate) {
          props.onUpdate();
        }
        
        toast.show({
          title: "Update Waitlist",
          content: "Waitlist has been successfully updated",
          duration: 10000,
          type: "success"
        });
      }).catch((error) => {
        console.error("Error updating waitlist:", error);
        setUpdatingShow(false);
        
        let errorMessage = "Failed to update waitlist";
        if (error?.response?.status === 401) {
          errorMessage = "Your session has expired. Please refresh the page and try again.";
        } else if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error?.message) {
          errorMessage = error.message;
        }
        
        toast.show({
          title: "Update Waitlist Failed",
          content: errorMessage,
          duration: 10000,
          type: "error"
        });
      });
    }
  }

  const getWaitlistSizingDetails = () => {
    let details: any = <p className={styles.details}>Per Level Limits lets you set a maximum entry limit, enable a waitlist and set a waitlist size for each individual level at your show.</p>;

    switch (waitlistSizing.id) {
      case Constants.waitlist_options.daily_judge_time_limit:
        details = <p className={styles.details}>Daily Judge Time Limit lets you decide the amount of entries based on the amount of time available to the judge on that day. The value is determined in minutes (ex. 480 minutes is 8 hours). You can also enable a waitlist and set the waitlist size if the time limit is reached.</p>;
      break;
      case Constants.waitlist_options.daily_trial_time_limit:
        details = <p className={styles.details}>Judge Hours Per Trial lets you decide the amount of entries based on the amount of time available to the trial on that day. The value is determined in minutes (ex. 480 minutes is 8 hours). You can also enable a waitlist and set the waitlist size if the time limit is reached.</p>;
        break;
      case Constants.waitlist_options.show_entry_limit:
        details = <p className={styles.details}>Show Entry Limit lets you set a maximum entry limit for the entire show, regardless of levels or judge hours. You can also enable a waitlist and set the waitlist size if the entry limit is reached.</p>;
      break;
      case Constants.waitlist_options.show_dog_limit:
        details = <p className={styles.details}>Show Dog Limit lets you set a maximum unique dogs limit for the entire show, regardless of levels or judge hours. You can also enable a waitlist and set the waitlist size if the entry limit is reached.</p>;
        break;
      case Constants.waitlist_options.daily_judge_mrv_limit:
        details = <p className={styles.details}>BHA Daily Judge MRV limits entries based on Maximum Run Value per judge per day. Each BHA level has a run-value multiplier (Instinct/Line Drive=1, Novice=1.5, Open=2, Senior/Crazy 8s=2.5, Master=3). The value entered is the maximum total run value allowed per judge (BHA default is 260). You can also enable a waitlist and set the waitlist size if the MRV limit is reached.</p>;
        break;
      case Constants.waitlist_options.entries_per_judge:
        details = <p className={styles.details}>Entries per Judge lets you set a maximum number of entries for each judge in this trial. Use Manual Entries to hold spots for mail-in entries: online entries close for a judge when they reach Total Entries minus Manual Entries. After the Manual Entry Limit Date/Time, the held spots open to everyone. Entries you make with "Register on behalf of" count as mail-in entries and can always use any open spot up to the judge's Total Entries. Every judge needs a Total Entries value.</p>;
        break;
      case Constants.waitlist_options.per_level_limit:
        if (isHerdingShow) {
          details = <p className={styles.details}>Per Stock Limits lets you set a maximum entry limit per stock type (Sheep, Cattle, Ducks) across all courses and levels for the day. Add only the stock types you want to limit — all others are unrestricted.</p>;
        } else {
          details = <p className={styles.details}>Per Level Limits lets you set a maximum entry limit, enable a waitlist and set a waitlist size for each individual level at your show.</p>;
        }
      break;
    }

    return details;
  }

  useEffect(() => {
    if (props.show.waitlist_limit !== undefined && props.show.waitlist_limit.toString().length > 0) {
      setWaitlistLimit(props.show.waitlist_limit.toString());
    } else if (waitlistSizing.id === Constants.waitlist_options.daily_judge_time_limit) {
      setWaitlistLimit((8 * 60).toString());
    } else if (waitlistSizing.id === Constants.waitlist_options.daily_trial_time_limit) {
      setWaitlistLimit((8 * 60).toString());
    } else if (waitlistSizing.id === Constants.waitlist_options.daily_judge_mrv_limit) {
      setWaitlistLimit((260).toString());
    } else if (waitlistSizing.id === Constants.waitlist_options.show_entry_limit) {
      setWaitlistLimit((500).toString());
    } else if (waitlistSizing.id === Constants.waitlist_options.show_dog_limit) {
      setWaitlistLimit((25).toString());
    }

    setShowWaitlistEnabled(props.show.waitlist_enabled || false);
    setShowWaitlistSize(props.show.waitlist_size?.toString() || "25");
  }, [waitlistSizing, props.show.waitlist_limit, props.show.waitlist_enabled, props.show.waitlist_size]);

  useEffect(() => {
    setDetectiveRunLimit(props.show.detective_run_limit?.toString() || "");
  }, [props.show.detective_run_limit]);

  useEffect(() => {
    setDetectiveManualEntries(props.show.detective_manual_entries?.toString() || "");
  }, [props.show.detective_manual_entries]);

  return (
    <form onSubmit={(e: any) => { e.preventDefault(); updateShowWaitlist(waitlists); }}className={styles.WaitlistTab}>
      <div className={styles.inputWrap}>
        <Dropdown value={waitlistSizing} onChange={(e: any, value: IOption) => { setWaitlistSizing(value); }} label="Limit Type" options={props.event.is_lottery ? lotteryWaitlistSizingOptions : waitlistSizingOptions} placeholder="" />
      </div>

      {offersDetectiveLimit && (
        <div className={styles.inputWrap}>
          <Input
            type="text"
            onChange={(e:any) => { setDetectiveRunLimit(e.target.value); }}
            label="Detective Run Limit"
            placeholder="Leave empty for no restriction"
            defaultValue={detectiveRunLimit}
          />
          <p className={styles.details}>Optional. Caps the number of Detective entries for this trial, independent of the Limit Type above (e.g. 125 entries total but only 10 Detective). Once reached, additional Detective entries follow the same Enable Waitlist / Waitlist Size settings configured for the Limit Type. Leave empty for no restriction.</p>
        </div>
      )}

      {showDetectiveManualEntries && (
        <div className={styles.inputWrap}>
          <Input
            type="number"
            min={0}
            onChange={(e:any) => { setDetectiveManualEntries(e.target.value); }}
            label="Detective Manual Entries"
            placeholder="Leave empty to hold no Detective spots"
            defaultValue={detectiveManualEntries}
            disabled={!(Number(detectiveRunLimit) > 0)}
          />
          <p className={styles.details}>Optional. Holds Detective spots for mail-in entries until the Manual Entry Limit Date/Time below. Online Detective entries close at the Detective Run Limit minus this number; after the date, the held spots open to everyone. Requires a Detective Run Limit.</p>
        </div>
      )}

      {/* Per-level / per-stock rows */}
      {waitlistSizing.id === Constants.waitlist_options.per_level_limit && waitlists.map((wl: IWaitlistItem, index: number) => {
        return(
          <WaitlistElement
            key={`${wl.show_element}_${wl.level}_${wl.section}_${index}`}
            waitlist={wl}
            index={index}
            isHerding={isHerdingShow}
            onDelete={isHerdingShow ? deleteHerdingRow : undefined}
            onChange={(_wl: IWaitlistItem, _index: number) => {
              updateWaitlist(_wl, _index);
            }}
          />);
      })}

      {/* Add-row UI for regular Herding (stock-only limit) */}
      {isHerdingShow && waitlistSizing.id === Constants.waitlist_options.per_level_limit && (
        <div className={styles.addHerdingRow}>
          <p className={styles.addHerdingRowLabel}>Add a stock limit row:</p>
          <div className={styles.addHerdingRowInputs}>
            <Dropdown
              value={newRowSection}
              onChange={(_e: any, value: IOption) => setNewRowSection(value)}
              label="Stock"
              options={herdingStockOptions.filter(o => !waitlists.some(w => w.section === o.id))}
              placeholder="Select stock type"
            />
            <button type="button" className={styles.addRowButton} onClick={addHerdingRow}>
              + Add Row
            </button>
          </div>
        </div>
      )}

      {isEntriesPerJudge && (
        <div className={styles.judgeLimits}>
          {judgeLimitRows.length === 0 ? (
            <p className={styles.warning}>Assign judges in the Judges or Judge Schedules tab before setting Entries per Judge.</p>
          ) : (
            <div className={styles.judgeLimitTable}>
              <div className={`${styles.judgeLimitRow} ${styles.judgeLimitHeader}`}>
                <div className={styles.judgeLimitName}>Judge</div>
                <div className={styles.judgeLimitInput}>Total Entries</div>
                <div className={styles.judgeLimitInput}>Manual Entries</div>
              </div>
              {judgeLimitRows.map((row, index) => {
                const missing = !row.total_entries;
                return (
                  <div
                    key={getJudgeEntryLimitKey(row.judge_name, row.judge_number)}
                    className={missing ? `${styles.judgeLimitRow} ${styles.missing}` : styles.judgeLimitRow}
                  >
                    <div className={styles.judgeLimitName}>
                      {row.judge_name}{row.judge_number ? ` (#${row.judge_number})` : ""}
                      {missing && <span className={styles.limitRequired}>Limit required</span>}
                    </div>
                    <div className={styles.judgeLimitInput}>
                      <Input
                        type="number"
                        min={1}
                        placeholder="Total"
                        defaultValue={row.total_entries}
                        onChange={(e: any) => updateJudgeLimitRow(index, "total_entries", e.target.value)}
                      />
                    </div>
                    <div className={styles.judgeLimitInput}>
                      <Input
                        type="number"
                        min={0}
                        placeholder="None"
                        defaultValue={row.manual_entries}
                        onChange={(e: any) => updateJudgeLimitRow(index, "manual_entries", e.target.value)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {classesWithoutJudge.length > 0 && (
            <p className={styles.warning}>
              {classesWithoutJudge.length} {classesWithoutJudge.length === 1 ? "class has" : "classes have"} no judge assigned. Online entries for them stay closed until a judge is assigned: {classesWithoutJudge.join(", ")}
            </p>
          )}

          {hasManualEntries && (
            <div className={styles.manualDate}>
              <DatePick
                label="Manual Entry Limit Date/Time"
                onChange={setManualEntryLimitDate}
                value={manualEntryLimitDate}
                showTimeSelect={true}
                required={true}
                rightText={timezone}
              />
              {manualEntryLimitDate && props.show.show_date &&
                moment.tz(moment(manualEntryLimitDate).format("YYYY-MM-DD HH:mm"), timezone).isAfter(moment(props.show.show_date)) && (
                <p className={styles.warning}>This date is after the trial starts, so held spots won't open to online entries before the trial.</p>
              )}
            </div>
          )}
        </div>
      )}

      {waitlistSizing.id !== Constants.waitlist_options.per_level_limit && <div className={styles.limitWrap}>
        {!isEntriesPerJudge && <Input
          type="text"
          required
          onChange={(e:any) => { setWaitlistLimit(e.target.value); }}
          label={
            (() => {
              if (waitlistSizing.id === Constants.waitlist_options.daily_judge_time_limit) {
                return "Total Judge Time (Minutes)";
              }
              if (waitlistSizing.id === Constants.waitlist_options.daily_trial_time_limit) {
                return "Total Trial Time (Minutes)";
              }
              if (waitlistSizing.id === Constants.waitlist_options.daily_judge_mrv_limit) {
                return "Max Run Value (MRV) Per Judge";
              }
              if (waitlistSizing.id === Constants.waitlist_options.show_dog_limit) {
                return "Dog Limit";
              }
              return "Entry Limit";
            })()
          }
          placeholder="" defaultValue={waitlistLimit}
        />}
        <div className={styles.waitlistControls}>
          <div className={styles.waitlistToggle}>
            <Checkbox 
              id="showWaitlistEnabled" 
              onChange={(e: any) => { setShowWaitlistEnabled(!showWaitlistEnabled); }} 
              value={showWaitlistEnabled} 
              label="Enable Waitlist" 
            />
          </div>
          {showWaitlistEnabled && (
            <div className={styles.waitlistSizeInput}>
              <Input 
                type="text" 
                required={showWaitlistEnabled} 
                onChange={(e: any) => { setShowWaitlistSize(e.target.value); }} 
                label="Waitlist Size" 
                placeholder="What's the waitlist size?" 
                defaultValue={showWaitlistSize} 
              />
            </div>
          )}
        </div>
      </div>}

      {getWaitlistSizingDetails()}

      <div className={styles.actions}>
        <Button type="submit" isLoading={updatingShow} disabled={isEntriesPerJudge && judgeLimitRows.length === 0}>Save</Button>
      </div>
    </form>
  );
};

export default WaitlistTab;
